<template>
  <v-card>
    <v-card-title>Activity Logs</v-card-title>
    <v-card-title class="pt-6">
      <v-container>
        <v-row align="center">
          <v-col
            class="col-md-2"
          >
            <refresh-button
              :loading="isLoading"
              @click="getLogs()"
            />
          </v-col>
          <v-col
            class="col-md-10"
          >
            <v-row dense>
              <v-col
                class="col-md-4"
              >
                <v-menu
                  v-model="fromDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filters.date_from"
                      label="From Date"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      dense
                      clearable
                      v-bind="attrs"
                      v-on="on"
                      @click:clear="fromDateMenu = false"
                    />
                  </template>
                  <v-date-picker
                    v-model="filters.date_from"
                    no-title
                    scrollable
                    @change="fromDateMenu = false"
                  />
                </v-menu>
              </v-col>
              <v-col
                class="col-md-4"
              >
                <v-menu
                  v-model="toDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filters.date_to"
                      label="To Date"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      dense
                      clearable
                      v-bind="attrs"
                      v-on="on"
                      @click:clear="toDateMenu = false"
                    />
                  </template>
                  <v-date-picker
                    v-model="filters.date_to"
                    no-title
                    scrollable
                    @change="toDateMenu = false"
                  />
                </v-menu>
              </v-col>
              <v-col
                class="col-md-4"
              >
                <v-text-field
                  v-model="search"
                  dense
                  label="Search..."
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  @keyup.enter="getLogs()"
                  @click:clear="onSearchClear()"
                />
              </v-col>
              <v-col
                class="col-md-4"
              >
                <v-select
                  v-model="filters.event"
                  :items="availableEvents"
                  label="Action"
                  dense
                  clearable
                  multiple
                  small-chips
                  deletable-chips
                  @change="getLogs()"
                />
              </v-col>
              <v-col
                class="col-md-4"
              >
                <v-select
                  v-model="filters.subject_type"
                  :items="availableSubjects"
                  label="Subject"
                  dense
                  clearable
                  multiple
                  small-chips
                  deletable-chips
                  @change="getLogs()"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="logs"
      :loading="isLoading"
      disable-pagination
      hide-default-footer
    >
      <template v-slot:progress>
        <v-progress-linear
          indeterminate
          class="mt-2"
          rounded
        />
        <p class="text-center mt-1">
          Loading... Please wait
        </p>
      </template>

      <template v-slot:body="{ items }">
        <tbody v-if="logs.length > 0">
          <tr
            v-for="log in items"
            :key="log.id"
          >
            <td class="text-no-wrap">
              {{ log.created_at | formatDateTime }}
            </td>
            <td class="text-capitalize">
              {{ log.event || log.description }}
            </td>
            <td>
              <span class="font-weight-medium">{{ log.subject_type | formatSubjectType }}</span>
              <span
                v-if="log.subject_identifier"
                class="ml-1 grey--text"
              >
                · {{ log.subject_identifier }}
              </span>
            </td>
            <td>{{ log.actor_name || (log.causer ? log.causer.name : 'System') }}</td>
            <td>
              <div class="d-flex align-center">
                <span
                  v-if="changedFields(log).length > 0"
                  class="caption mr-1"
                >
                  {{ changedFields(log).slice(0, 2).map(k => formatKey(k)).join(', ') }}<span
                    v-if="changedFields(log).length > 2"
                    class="grey--text"
                  > +{{ changedFields(log).length - 2 }}</span>
                </span>
                <span
                  v-else-if="hasPreviousValues(log)"
                  class="caption grey--text mr-1"
                >
                  No field changes recorded
                </span>
                <span
                  v-else-if="hasAttributes(log)"
                  class="caption grey--text mr-1"
                >
                  {{ Object.keys(log.properties.attributes).length }} fields
                </span>
                <v-btn
                  icon
                  small
                  @click="viewDetails(log)"
                >
                  <v-icon small>
                    {{ icons.mdiEye }}
                  </v-icon>
                </v-btn>
              </div>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr v-show="!isLoading">
            <td
              :colspan="headers.length"
              style="text-align: center"
            >
              No activity logs found
            </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>

    <data-table-pagination
      :page="pagination.page"
      :page-count="pagination.pageCount"
      @page-change="onPageChange"
      @items-per-page-change="onItemsPerPageChange"
    />

    <v-dialog
      v-model="detailsDialog"
      max-width="750"
    >
      <v-card v-if="selectedLog">
        <v-card-title class="pb-2">
          <span class="subtitle-1">Activity Details</span>
          <v-spacer />
          <v-btn
            icon
            @click="detailsDialog = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pt-4">
          <v-row
            dense
            class="mb-1"
          >
            <v-col class="col-md-3 col-6">
              <div class="caption grey--text text--darken-1">
                Date
              </div>
              <div class="body-2">
                {{ selectedLog.created_at | formatDateTime }}
              </div>
            </v-col>
            <v-col class="col-md-3 col-6">
              <div class="caption grey--text text--darken-1">
                Performed By
              </div>
              <div class="body-2">
                {{ selectedLog.actor_name || (selectedLog.causer ? selectedLog.causer.name : 'System') }}
              </div>
            </v-col>
            <v-col
              v-if="selectedLog.subject_id"
              class="col-md-3 col-6"
            >
              <div class="caption grey--text text--darken-1">
                Subject
              </div>
              <div class="body-2">
                {{ selectedLog.subject_type | formatSubjectType }}
                <span
                  v-if="selectedLog.subject_identifier"
                  class="font-weight-medium"
                >
                  · {{ selectedLog.subject_identifier }}
                </span>
              </div>
            </v-col>
          </v-row>

          <v-row
            v-if="selectedLog.event || selectedLog.description"
            dense
            class="mb-1"
          >
            <v-col cols="12">
              <div class="caption grey--text text--darken-1">
                Action
              </div>
              <div class="body-2 text-capitalize">
                {{ selectedLog.event || selectedLog.description }}
              </div>
            </v-col>
          </v-row>

          <v-row
            v-if="selectedLog.subject_context && selectedLog.subject_context.length > 0"
            dense
            class="mb-3"
          >
            <v-col
              v-for="item in selectedLog.subject_context"
              :key="item.label"
              class="col-md-3 col-6"
            >
              <div class="caption grey--text text--darken-1">
                {{ item.label }}
              </div>
              <div class="body-2 font-weight-medium">
                {{ item.value || '—' }}
              </div>
            </v-col>
          </v-row>

          <v-divider class="mb-4" />

          <!-- Update: only changed fields -->
          <template v-if="changedFields(selectedLog).length > 0">
            <div class="subtitle-2 mb-3">
              Changes
              <span class="caption grey--text ml-1">
                ({{ changedFields(selectedLog).length }} field{{ changedFields(selectedLog).length !== 1 ? 's' : '' }})
              </span>
            </div>
            <v-simple-table dense>
              <thead>
                <tr class="grey lighten-4">
                  <th class="text-left">
                    Field
                  </th>
                  <th class="text-left red--text text--darken-1">
                    Before
                  </th>
                  <th class="text-left green--text text--darken-2">
                    After
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="field in changedFields(selectedLog)"
                  :key="field"
                >
                  <td class="font-weight-medium">
                    {{ formatFieldName(field) }}
                  </td>
                  <td class="red--text text--lighten-2">
                    {{ resolveValue(selectedLog, field, selectedLog.properties.old[field]) }}
                  </td>
                  <td class="green--text text--darken-1">
                    {{ resolveValue(selectedLog, field, selectedLog.properties.attributes[field]) }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </template>

          <p
            v-else-if="hasPreviousValues(selectedLog)"
            class="grey--text text-center mt-4"
          >
            No field changes recorded.
          </p>

          <!-- Create: attributes table -->
          <template v-else-if="hasAttributes(selectedLog)">
            <div class="subtitle-2 mb-3">
              Recorded Values
            </div>
            <v-simple-table dense>
              <thead>
                <tr class="grey lighten-4">
                  <th class="text-left">
                    Field
                  </th>
                  <th class="text-left">
                    Value
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(val, key) in selectedLog.properties.attributes"
                  :key="key"
                >
                  <td class="font-weight-medium">
                    {{ formatFieldName(key) }}
                  </td>
                  <td>
                    {{ resolveValue(selectedLog, key, val) }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </template>

          <!-- Custom/manual properties -->
          <template v-else-if="hasProperties(selectedLog)">
            <div class="subtitle-2 mb-3">
              Details
            </div>
            <v-simple-table dense>
              <thead>
                <tr class="grey lighten-4">
                  <th class="text-left">
                    Field
                  </th>
                  <th class="text-left">
                    Value
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(val, key) in filteredProperties(selectedLog)"
                  :key="key"
                >
                  <td class="font-weight-medium">
                    {{ key | formatKey }}
                  </td>
                  <td>
                    {{ formatValue(val) }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </template>

          <p
            v-else
            class="grey--text text-center mt-4"
          >
            No additional details recorded.
          </p>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="detailsDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import axios from 'axios'
import _ from 'lodash'
import moment from 'moment'
import { mdiEye, mdiClose } from '@mdi/js'
import RefreshButton from '@/components/partials/RefreshButton.vue'
import DataTablePagination from '@/components/partials/DataTablePagination.vue'

export default {
  components: {
    RefreshButton,
    DataTablePagination,
  },
  filters: {
    formatDateTime(value) {
      return value ? moment(String(value)).format('DD/MM/YYYY HH:mm') : '—'
    },
    formatSubjectType(value) {
      if (!value) return '—'
      const parts = value.split('\\')
      if (parts[parts.length - 1] === 'MpesaTransaction') return 'Payment'

      return parts[parts.length - 1].replace(/([A-Z])/g, ' $1').trim()
    },
    formatKey(value) {
      return value
        ? value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        : value
    },
  },
  props: {
    userId: {
      type: [String, Number],
      default: null,
    },
    meterId: {
      type: [String, Number],
      default: null,
    },
    daysBack: {
      type: Number,
      default: 30,
    },
  },
  data() {
    return {
      logs: [],
      isLoading: false,
      detailsDialog: false,
      selectedLog: null,
      search: '',
      fromDateMenu: false,
      toDateMenu: false,
      filters: {
        date_from: moment().subtract(this.daysBack, 'days').format('YYYY-MM-DD'),
        date_to: moment().format('YYYY-MM-DD'),
        event: [],
        subject_type: [],
      },
      availableEvents: [],
      availableSubjects: [],
      pagination: {
        page: 1,
        pageCount: 1,
        itemsPerPage: 10,
      },
      headers: [
        { text: 'Date & Time', value: 'created_at', sortable: false },
        { text: 'Action', value: 'event', sortable: false },
        { text: 'Subject', value: 'subject_type', sortable: false },
        { text: 'Performed By', value: 'causer', sortable: false },
        { text: 'Details', value: 'actions', sortable: false },
      ],
      icons: {
        mdiEye,
        mdiClose,
      },
    }
  },
  computed: {
    refreshTable() {
      return `${this.userId}|${this.meterId}|${this.search}|${JSON.stringify(this.filters)}|${this.pagination.itemsPerPage}`
    },
  },
  watch: {
    refreshTable() {
      this.pagination.page = 1
      this.getLogs()
    },
  },
  created() {
    this.getLogs = _.debounce(this.getLogs, 500)
  },
  mounted() {
    this.getLogs()
    this.fetchFilterOptions()
  },
  beforeDestroy() {
    this.getLogs.cancel()
  },
  methods: {
    getLogs() {
      this.isLoading = true
      axios
        .get(this.buildUrl())
        .then(response => {
          this.logs = response.data.data
          this.pagination.page = response.data.current_page
          this.pagination.pageCount = response.data.last_page
          this.pagination.itemsPerPage = response.data.per_page
          this.isLoading = false
        })
        .catch(error => {
          this.isLoading = false
          this.notifyError(error)
        })
    },
    notifyError(error) {
      this.$notification.error((error.response && error.response.data && error.response.data.message)
        || 'Unable to load activity logs. Please try again.')
    },
    buildUrl() {
      const params = new URLSearchParams()
      params.append('page', this.pagination.page)
      params.append('per_page', this.pagination.itemsPerPage)
      if (this.filters.date_from) params.append('date_from', this.filters.date_from)
      if (this.filters.date_to) params.append('date_to', this.filters.date_to)
      if (this.search) params.append('search', this.search)
      if (this.userId) params.append('subject_user_id', this.userId)
      if (this.meterId) params.append('subject_meter_id', this.meterId)
      if (this.filters.event && this.filters.event.length) {
        this.filters.event.forEach(e => params.append('event[]', e))
      }
      if (this.filters.subject_type && this.filters.subject_type.length) {
        this.filters.subject_type.forEach(t => params.append('subject_type[]', t))
      }

      return `activity-logs?${params.toString()}`
    },
    fetchFilterOptions() {
      axios.get('activity-logs/events').then(res => {
        this.availableEvents = res.data.map(e => ({ text: e.charAt(0).toUpperCase() + e.slice(1), value: e }))
      }).catch(this.notifyError)
      axios.get('activity-logs/subject-types').then(res => {
        this.availableSubjects = res.data.map(type => {
          const label = this.$options.filters.formatSubjectType(type)

          return { text: label, value: type }
        })
      }).catch(this.notifyError)
    },
    onPageChange(page) {
      this.pagination.page = page
      this.getLogs()
    },
    onItemsPerPageChange(itemsPerPage) {
      this.pagination.itemsPerPage = itemsPerPage
      this.getLogs()
    },
    onSearchClear() {
      this.search = ''
      this.getLogs()
    },
    viewDetails(log) {
      this.selectedLog = log
      this.detailsDialog = true
    },
    changedFields(log) {
      if (!log.properties || !log.properties.old || !log.properties.attributes) return []

      return Object.keys(log.properties.old).filter(
        key => JSON.stringify(log.properties.old[key]) !== JSON.stringify(log.properties.attributes[key]),
      )
    },
    formatKey(value) {
      return value
        ? value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        : value
    },
    formatFieldName(key) {
      const labels = {
        TransID: 'Payment Reference',
        TransAmount: 'Amount (Ksh)',
        BillRefNumber: 'Account Number',
        user_id: 'Customer',
        meter_id: 'Meter',
        station_id: 'Meter Station',
        mpesa_transaction_id: 'M-Pesa Transaction',
      }

      return labels[key] || this.formatKey(key)
    },
    resolveValue(log, field, val) {
      const resolved = log.properties && log.properties.resolved
      if (resolved && resolved[field] && val !== null && val !== undefined) {
        const label = resolved[field][val]
        if (label !== undefined) return label
      }

      return this.formatValue(val, field)
    },
    formatValue(val, field = null) {
      if (val === null || val === undefined) return '—'

      // Handle JS boolean
      if (typeof val === 'boolean') return val ? 'Yes' : 'No'

      // Handle 0/1 integers stored by MySQL for boolean columns
      if (field && this.isBooleanField(field) && (val === 0 || val === 1 || val === '0' || val === '1')) {
        return Number(val) === 1 ? 'Yes' : 'No'
      }

      // Format ISO date/datetime strings
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
        const m = moment(val)
        if (m.isValid()) {
          if (field === 'month') return m.format('MMM YYYY')

          return val.length > 10 ? m.format('DD/MM/YYYY HH:mm') : m.format('DD/MM/YYYY')
        }
      }

      if (typeof val === 'object') return JSON.stringify(val)

      return String(val)
    },
    isBooleanField(field) {
      return /^(is_|has_|can_|show_|hide_)/.test(field)
        || /(_active|_enabled|_verified|_visible|_confirmed|_approved|_blocked)$/.test(field)
    },
    hasPreviousValues(log) {
      return log.properties && log.properties.old && Object.keys(log.properties.old).length > 0
    },
    hasAttributes(log) {
      return log.properties && log.properties.attributes && Object.keys(log.properties.attributes).length > 0
    },
    hasProperties(log) {
      if (!log.properties) return false
      const keys = Object.keys(log.properties).filter(k => k !== 'causer_details')

      return keys.length > 0 && !keys.includes('attributes') && !keys.includes('old')
    },
    filteredProperties(log) {
      if (!log.properties) return {}
      const result = { ...log.properties }
      delete result.causer_details

      return result
    },
  },
}
</script>
